import React, { useRef, useEffect, useState } from 'react';

import { Product } from '../hooks/useProducts';
import { useAppDispatch, useAppSelector } from '../store/hooks';
import {
  selectIsProductConfirmed,
  selectSelectedProductVariants,
  selectIsProductConfirmationMode,
} from '../store/selectors';
import { setIsProductConfirmationMode } from '../store/slices/confirmationSlice';
import { formatPrice } from '../utils/Formatting';
import { useSettingsRedux } from '../hooks/useSettingsRedux';
import './SelectedProductCompactView.css';
import closeIcon from '../assets/close.png';

interface SelectedProductCompactViewProps {
  product: Product;
  walletAmount: number;
}

const SelectedProductCompactView: React.FC<SelectedProductCompactViewProps> = ({ product, walletAmount }) => {
  const dispatch = useAppDispatch();
  const isProductConfirmed = useAppSelector(selectIsProductConfirmed);
  const isProductConfirmationMode = useAppSelector(selectIsProductConfirmationMode);
  const selectedVariants = useAppSelector(selectSelectedProductVariants);
  const discountElementRef = useRef<HTMLSpanElement>(null);
  const [discountPulse, setDiscountPulse] = React.useState(false);
  const [animatedDiscountAmount, setAnimatedDiscountAmount] = useState(0);
  const [flyingDiscounts, setFlyingDiscounts] = React.useState<Array<{
    key: string;
    id: string;
    imageUrl: string;
    top: number;
    left: number;
    targetX: number;
    targetY: number;
    animationDelay: string;
    animationDuration: string;
  }> | null>(null);

  // Translation labels
  const { settings } = useSettingsRedux();
  const productTranslations = (settings?.translations?.product ?? {}) as Record<string, string>;
  const labelPrice = productTranslations.price ?? 'Price';
  const labelDiscount = productTranslations.discount ?? 'Discount';
  const labelTotal = productTranslations.total ?? 'Total';

  // Add useEffect to handle synchronized discount animation
  useEffect(() => {
    if (isProductConfirmationMode && !isProductConfirmed) {
      // Start the discount count-up animation with 500ms delay (synchronized with wallet countdown)
      setAnimatedDiscountAmount(0);

      if (walletAmount > 0) {
        const delayTimeout = setTimeout(() => {
          const interval = setInterval(() => {
            setAnimatedDiscountAmount((prev) => {
              const next = prev + 1;
              if (next >= walletAmount) {
                clearInterval(interval);
                return walletAmount;
              }
              return next;
            });
          }, 75);
        }, 1500);

        return () => {
          clearTimeout(delayTimeout);
        };
      }
    } else {
      // Reset animated discount when not in confirmation mode
      setAnimatedDiscountAmount(0);
    }
  }, [isProductConfirmationMode, isProductConfirmed, walletAmount]);

  // Manual animation function that finds the wallet element
  const triggerWalletToDiscountAnimation = React.useCallback(() => {
    // Find wallet element by class name instead of using ref
    const walletElement = document.querySelector('.playmerce-game-statusbar-wallet') as HTMLElement;
    const discountElement = discountElementRef.current;

    if (!walletElement || !discountElement) {
      console.warn('Wallet or discount element not found for animation', {
        walletElement: !!walletElement,
        discountElement: !!discountElement,
      });
      return;
    }

    const walletRect = walletElement.getBoundingClientRect();
    const discountRect = discountElement.getBoundingClientRect();

    // Get scroll offset from the routes container
    const routesContainer = document.getElementById('routes-container');
    const scrollTop = routesContainer?.scrollTop || 0;

    // Calculate start position (wallet center)
    const walletCenterX = walletRect.left + walletRect.width / 2;
    const walletCenterY = walletRect.top + walletRect.height / 2;

    // Calculate target position (discount element center)
    const discountCenterX = discountRect.left + discountRect.width / 2;
    const discountCenterY = discountRect.top + discountRect.height / 2 + scrollTop;

    // Calculate translation vector from wallet to discount
    const targetX = discountCenterX - walletCenterX;
    const targetY = discountCenterY - walletCenterY;

    // Create flying discounts with staggered animations
    const newFlyingDiscounts = [
      {
        key: `discount-1-${Date.now()}`,
        id: 'wallet-to-discount',
        imageUrl: '/img/diamond.png',
        top: walletCenterY,
        left: walletCenterX,
        targetX,
        targetY,
        animationDelay: '0s',
        animationDuration: '0.8s',
      },
      {
        key: `discount-2-${Date.now()}`,
        id: 'wallet-to-discount',
        imageUrl: '/img/diamond.png',
        top: walletCenterY + 3,
        left: walletCenterX + 3,
        targetX,
        targetY,
        animationDelay: '0.15s',
        animationDuration: '0.7s',
      },
      {
        key: `discount-3-${Date.now()}`,
        id: 'wallet-to-discount',
        imageUrl: '/img/diamond.png',
        top: walletCenterY - 3,
        left: walletCenterX - 3,
        targetX,
        targetY,
        animationDelay: '0.3s',
        animationDuration: '0.9s',
      },
    ];

    // Start the animations
    setFlyingDiscounts(newFlyingDiscounts);

    // Trigger discount element pulse after the last discount reaches it
    setTimeout(() => {
      setDiscountPulse(true);
      setTimeout(() => setDiscountPulse(false), 400);
    }, 900); // After the longest animation duration

    // Clear flying discounts after all animations complete
    setTimeout(() => setFlyingDiscounts(null), 1200);
  }, []);

  // Trigger animation when component mounts
  useEffect(() => {
    // Delay to ensure the compact view transition is complete and elements are fully rendered
    // The compact view appears after 450ms transition, so we add significant delay for the user to see the compact view
    const timer = setTimeout(() => {
      triggerWalletToDiscountAnimation();
    }, 1000); // 1 second delay after compact view appears to let user see the view first

    return () => clearTimeout(timer);
  }, [triggerWalletToDiscountAnimation]);

  if (!product || isProductConfirmed) {
    return null; // Hide the component when product is confirmed
  }

  const handleCancel = () => {
    dispatch(setIsProductConfirmationMode(false));
  };

  const originalPrice = parseFloat(product.priceRange.minVariantPrice.amount);
  const calculatedSubtotal = Math.max(0, originalPrice - animatedDiscountAmount);
  const currencyCode = product.priceRange.minVariantPrice.currencyCode;

  // Helper function to format variants for display
  const formatVariantsDisplay = () => {
    if (!selectedVariants || Object.keys(selectedVariants).length === 0) {
      return null;
    }

    return Object.entries(selectedVariants).map(([key, value]) => (
      <span key={key} className="compact-variant-item">
        {key}: {value}
      </span>
    ));
  };

  return (
    <>
      <div className="selected-product-compact-view">
        <button id="cancel-button" className="cancel-button text-button" onClick={handleCancel}>
          <img src={closeIcon} alt="Close" />
        </button>
        <div className="compact-product-details-row">
          <div className="compact-product-image-container">
            {product.featuredImage ? (
              <img
                src={product.featuredImage.url}
                alt={product.featuredImage.altText || product.title}
                className="compact-product-image"
              />
            ) : (
              <div className="compact-no-image-placeholder"></div>
            )}
          </div>
          <div className="compact-product-info">
            <h3 className="compact-product-title">{product.title}</h3>
            {selectedVariants && Object.keys(selectedVariants).length > 0 && (
              <div className="compact-product-variants">{formatVariantsDisplay()}</div>
            )}
            <div className="compact-product-price">
              <p className="compact-product-price-row compact-product-price-original">
                {formatPrice(
                  product.priceRange.minVariantPrice.amount,
                  product.priceRange.minVariantPrice.currencyCode
                )}
              </p>
              <p className="compact-product-price-row">
                <span className="compact-product-discount">{labelDiscount}: </span>
                <span ref={discountElementRef} className={`compact-product-discount`}>
                  -{formatPrice(animatedDiscountAmount.toString(), currencyCode)}
                </span>
              </p>
              <p className="compact-product-price-row compact-product-price-total">
                {labelPrice}: {formatPrice(calculatedSubtotal.toString(), currencyCode)}
              </p>
            </div>
          </div>
        </div>
      </div>
      {/* Render flying discounts for this component */}
      {flyingDiscounts &&
        flyingDiscounts.map((discount) => (
          <img
            key={discount.key}
            src={discount.imageUrl}
            alt=""
            className="flying-discount"
            style={
              {
                top: `${discount.top}px`,
                left: `${discount.left}px`,
                '--target-x': `${discount.targetX}px`,
                '--target-y': `${discount.targetY}px`,
                animationDelay: discount.animationDelay,
                animationDuration: discount.animationDuration,
              } as React.CSSProperties
            }
          />
        ))}
    </>
  );
};

export default SelectedProductCompactView;
